import {
  areCredentialOverridesValid,
  PersonalAuthCredentialOverrides,
} from "@app/components/oauth/PersonalAuthCredentialOverrides";
import { useSendNotification } from "@app/hooks/useNotification";
import { getMcpServerViewDisplayName } from "@app/lib/actions/mcp_helper";
import { getAvatar } from "@app/lib/actions/mcp_icons";
import type { MCPServerViewType } from "@app/lib/api/mcp";
import { getMcpServerViewDisplayDescription } from "@app/lib/dust_provided_descriptions";
import { compareStrings } from "@app/lib/i18n/format";
import type { MCPServerConnectionType } from "@app/lib/resources/mcp_server_connection_resource";
import {
  useCreatePersonalConnection,
  useDeleteMCPServerConnection,
  useMCPServerConnections,
  useMCPServerViews,
  useMCPServerViewsFromSpaces,
} from "@app/lib/swr/mcp_servers";
import { useSpaces } from "@app/lib/swr/spaces";
import { useDeleteToolApproval, useUserApprovals } from "@app/lib/swr/user";
import { classNames } from "@app/lib/utils";
import type { OAuthCredentialInputs } from "@app/types/oauth/lib";
import { getOverridablePersonalAuthInputs } from "@app/types/oauth/lib";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Button,
  Chip,
  CloudArrowLeftRight,
  DataTable,
  Dialog,
  DialogContainer,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DotsHorizontal,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  LoadingBlock,
  SearchInput,
} from "@dust-tt/sparkle";
import { Trans, useLingui } from "@lingui/react/macro";
import type { ColumnDef } from "@tanstack/react-table";
import keyBy from "lodash/keyBy";
import { useCallback, useMemo, useState } from "react";

interface UserTableRow {
  id: string;
  name: string;
  description: string;
  serverView: MCPServerViewType;
  connection: MCPServerConnectionType | undefined;
  canConnect: boolean;
  hasApproval: boolean;
  visual: React.JSX.Element;
  onClick?: () => void;
}

interface UserToolsTableProps {
  owner: LightWorkspaceType;
}

interface UserToolSkeletonRow {
  actions: string;
  name: string;
  onClick?: () => void;
}

const USER_TOOL_SKELETON_ROWS: UserToolSkeletonRow[] = Array.from(
  { length: 5 },
  (_, index) => ({
    actions: `action-${index}`,
    name: `tool-${index}`,
  })
);

function getUserToolSkeletonColumns(
  nameHeader: string
): ColumnDef<UserToolSkeletonRow>[] {
  return [
    {
      accessorKey: "name",
      header: nameHeader,
      cell: ({ row }) => (
        <DataTable.CellContent grow>
          <div className="flex flex-row items-center gap-3 py-3">
            <LoadingBlock className="h-9 w-9 shrink-0 rounded-lg" />
            <div className="flex min-w-0 flex-grow flex-col overflow-hidden">
              <div className="flex h-5 items-center">
                <LoadingBlock
                  className={classNames(
                    "h-4 max-w-full",
                    ["w-28", "w-36", "w-24", "w-32", "w-40"][row.index]
                  )}
                />
              </div>
              <div className="flex h-5 items-center">
                <LoadingBlock
                  className={classNames(
                    "h-4 max-w-full",
                    ["w-64", "w-52", "w-72", "w-60", "w-56"][row.index]
                  )}
                />
              </div>
            </div>
            <LoadingBlock className="h-6 w-20 shrink-0 rounded-[9px]" />
          </div>
        </DataTable.CellContent>
      ),
      meta: {
        className: "w-full",
      },
    },
    {
      accessorKey: "actions",
      header: "",
      cell: () => (
        <DataTable.CellContent>
          <LoadingBlock className="h-8 w-8 rounded-xl" />
        </DataTable.CellContent>
      ),
      meta: {
        className: "w-12",
      },
    },
  ];
}

function UserToolsTableSkeleton() {
  const { t } = useLingui();
  const columns = useMemo(() => getUserToolSkeletonColumns(t`Name`), [t]);
  return (
    <div aria-hidden="true">
      <DataTable
        data={USER_TOOL_SKELETON_ROWS}
        columns={columns}
        sorting={[{ id: "name", desc: false }]}
      />
    </div>
  );
}

export function UserToolsTable({ owner }: UserToolsTableProps) {
  const { t } = useLingui();
  const sendNotification = useSendNotification();
  const [searchQuery, setSearchQuery] = useState("");

  const { spaces } = useSpaces({
    workspaceId: owner.sId,
    kinds: ["global", "regular"],
  });
  const globalSpace = spaces.find((space) => space.kind === "global");
  const { serverViews, isLoading: isMCPServerViewsLoading } =
    useMCPServerViewsFromSpaces(owner, spaces);
  const {
    serverViews: hiddenServerViews,
    isMCPServerViewsLoading: isHiddenMCPServerViewsLoading,
  } = useMCPServerViews({
    owner,
    space: globalSpace,
    availability: "auto_hidden_builder",
    disabled: !globalSpace,
  });
  const { connections, isConnectionsLoading } = useMCPServerConnections({
    owner,
    connectionType: "personal",
  });
  const { approvals, isApprovalsLoading, mutateApprovals } =
    useUserApprovals(owner);
  const { deleteToolApproval } = useDeleteToolApproval();

  const handleDeleteToolMetadata = useCallback(
    async (mcpServerId: string) => {
      const response = await deleteToolApproval(owner, mcpServerId);
      if (response && !response.ok) {
        sendNotification({
          title: t`Error`,
          description: t`Failed to delete tool approbation history.`,
          type: "error",
        });
        return;
      }

      await mutateApprovals();
      sendNotification({
        title: t`Success!`,
        description: t`Tool approbation history deleted.`,
        type: "success",
      });
    },
    [sendNotification, deleteToolApproval, mutateApprovals, owner, t]
  );

  const { deleteMCPServerConnection } = useDeleteMCPServerConnection({
    owner,
  });

  const { createPersonalConnection } = useCreatePersonalConnection(owner);
  const [connectingServerId, setConnectingServerId] = useState<string | null>(
    null
  );

  // Providers with user-overridable credentials, collect them in a dialog
  // before starting the OAuth flow.
  const [overridesDialog, setOverridesDialog] = useState<{
    serverView: MCPServerViewType;
    inputs: OAuthCredentialInputs;
  } | null>(null);
  const [overriddenCredentials, setOverriddenCredentials] = useState<
    Record<string, string>
  >({});

  const handleConnect = useCallback(
    async (
      serverView: MCPServerViewType,
      overriddenCredentials?: Record<string, string>
    ) => {
      const { authorization } = serverView.server;
      if (!authorization) {
        return;
      }
      setConnectingServerId(serverView.server.sId);
      try {
        const result = await createPersonalConnection({
          mcpServerId: serverView.server.sId,
          mcpServerDisplayName: getMcpServerViewDisplayName(serverView),
          authorization,
          provider: authorization.provider,
          useCase: "personal_actions",
          scope: authorization.scope,
          overriddenCredentials:
            overriddenCredentials &&
            Object.keys(overriddenCredentials).length > 0
              ? overriddenCredentials
              : undefined,
        });
        if (!result.success && result.error) {
          sendNotification({
            type: "error",
            title: t`Failed to connect provider`,
            description: result.error,
          });
        }
      } finally {
        setConnectingServerId(null);
      }
    },
    [createPersonalConnection, sendNotification, t]
  );

  const handleConnectClick = useCallback(
    (serverView: MCPServerViewType) => {
      const provider = serverView.server.authorization?.provider;
      const inputs = provider
        ? getOverridablePersonalAuthInputs({ provider })
        : null;
      if (!inputs) {
        void handleConnect(serverView);
        return;
      }
      setOverriddenCredentials({});
      setOverridesDialog({ serverView, inputs });
    },
    [handleConnect]
  );

  // Prepare data for the actions table
  const actionsTableData = useMemo(() => {
    const connectionsByServerId = keyBy(
      connections,
      (c) => c.internalMCPServerId ?? `${c.remoteMCPServerId}`
    );

    const approvalServerIds = new Set(
      approvals.map((approval) => approval.mcpServerId)
    );

    const isConnectable = (serverView: MCPServerViewType) =>
      serverView.oAuthUseCase === "personal_actions" &&
      !!serverView.server.authorization;

    return [...serverViews, ...hiddenServerViews]
      .filter((serverView) => {
        // Include servers that have approvals, connections, or that the user
        // could connect to (personal-auth tools available in the workspace).
        const hasConnection = !!connectionsByServerId[serverView.server.sId];
        const hasApproval = approvalServerIds.has(serverView.server.sId);
        return hasConnection || hasApproval || isConnectable(serverView);
      })
      .filter(
        (serverView) =>
          (serverView.name ?? serverView.server.name)
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          getMcpServerViewDisplayDescription(t, serverView)
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
      )
      .map((serverView) => ({
        id: serverView.sId,
        name: getMcpServerViewDisplayName(serverView),
        description: getMcpServerViewDisplayDescription(t, serverView),
        serverView: serverView,
        connection: connectionsByServerId[serverView.server.sId],
        canConnect: isConnectable(serverView),
        hasApproval: approvalServerIds.has(serverView.server.sId),
        visual: getAvatar(serverView.server),
        onClick: () => {},
      }));
  }, [serverViews, hiddenServerViews, connections, approvals, searchQuery, t]);

  // Define columns for the actions table
  const actionColumns = useMemo<ColumnDef<UserTableRow>[]>(
    () => [
      {
        accessorKey: "name",
        header: t`Name`,
        sortingFn: (rowA, rowB) => {
          return compareStrings(rowA.original.name, rowB.original.name);
        },
        cell: ({ row }) => (
          <DataTable.CellContent grow>
            <div
              className={classNames("flex flex-row items-center gap-3 py-3")}
            >
              {getAvatar(row.original.serverView.server)}
              <div className="flex flex-grow flex-col gap-0 overflow-hidden truncate">
                <div className="truncate text-sm font-semibold text-foreground">
                  {getMcpServerViewDisplayName(row.original.serverView)}
                </div>
                <div className="truncate text-sm text-muted-foreground">
                  {getMcpServerViewDisplayDescription(
                    t,
                    row.original.serverView
                  )}
                </div>
              </div>

              {row.original.connection && (
                <Chip color="success" size="xs">
                  <Trans>Connected</Trans>
                </Chip>
              )}
              {!row.original.connection && row.original.canConnect && (
                <Button
                  icon={CloudArrowLeftRight}
                  size="xs"
                  variant="outline"
                  label={t`Connect`}
                  disabled={connectingServerId !== null}
                  isLoading={
                    connectingServerId === row.original.serverView.server.sId
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConnectClick(row.original.serverView);
                  }}
                />
              )}
            </div>
          </DataTable.CellContent>
        ),
        meta: {
          className: "w-full",
        },
      },
      {
        header: "",
        accessorKey: "actions",
        cell: ({ row }) =>
          (row.original.hasApproval || row.original.connection) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  aria-label={t`More options`}
                  icon={DotsHorizontal}
                  size="icon"
                  variant="ghost-secondary"
                  onClick={(e) => e.stopPropagation()}
                />
              </DropdownMenuTrigger>
              <DropdownMenuPortal>
                <DropdownMenuContent align="end">
                  <DropdownMenuGroup>
                    {row.original.hasApproval && (
                      <DropdownMenuItem
                        label={t`Clear confirmation preferences`}
                        onClick={() =>
                          handleDeleteToolMetadata(
                            row.original.serverView.server.sId
                          )
                        }
                      />
                    )}
                    {row.original.connection && (
                      <DropdownMenuItem
                        label={t`Disconnect`}
                        onClick={() =>
                          deleteMCPServerConnection({
                            connection: row.original.connection!,
                            mcpServer: row.original.serverView.server,
                          })
                        }
                      />
                    )}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenuPortal>
            </DropdownMenu>
          ),
        meta: {
          className: "w-12",
        },
      },
    ],
    [
      deleteMCPServerConnection,
      handleDeleteToolMetadata,
      handleConnectClick,
      connectingServerId,
      t,
    ]
  );

  const overridesDialogServerName = overridesDialog
    ? getMcpServerViewDisplayName(overridesDialog.serverView)
    : null;

  return (
    <>
      <div className="relative my-4">
        <SearchInput
          name="search"
          placeholder={t`Search tools`}
          value={searchQuery}
          onChange={setSearchQuery}
        />
      </div>

      {isMCPServerViewsLoading ||
      isHiddenMCPServerViewsLoading ||
      isConnectionsLoading ||
      isApprovalsLoading ? (
        <UserToolsTableSkeleton />
      ) : actionsTableData.length > 0 ? (
        <DataTable
          data={actionsTableData}
          columns={actionColumns}
          sorting={[{ id: "name", desc: false }]}
        />
      ) : (
        <p className="py-8 text-center text-muted-foreground">
          {searchQuery
            ? t`No matching tools found`
            : t`No tools available yet.`}
        </p>
      )}

      <Dialog
        open={overridesDialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setOverridesDialog(null);
          }
        }}
      >
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle
              visual={
                overridesDialog && getAvatar(overridesDialog.serverView.server)
              }
            >
              {overridesDialogServerName &&
                t`Connect ${overridesDialogServerName}`}
            </DialogTitle>
          </DialogHeader>
          <DialogContainer>
            {overridesDialog && (
              <PersonalAuthCredentialOverrides
                inputs={overridesDialog.inputs}
                values={overriddenCredentials}
                idPrefix={overridesDialog.serverView.server.sId}
                onChange={(key, value) =>
                  setOverriddenCredentials((prev) => ({
                    ...prev,
                    [key]: value,
                  }))
                }
              />
            )}
          </DialogContainer>
          <DialogFooter
            leftButtonProps={{
              label: t`Cancel`,
              variant: "outline",
            }}
            rightButtonProps={{
              label: t`Connect`,
              variant: "primary",
              disabled: !areCredentialOverridesValid(
                overridesDialog?.inputs,
                overriddenCredentials
              ),
              onClick: () => {
                if (!overridesDialog) {
                  return;
                }
                setOverridesDialog(null);
                void handleConnect(
                  overridesDialog.serverView,
                  overriddenCredentials
                );
              },
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
